import type { ReactNode } from "react";
import {
  Image,
  getSrcSet,
  type FitOptions,
  type ImageProps,
} from "~/components/ui/Image";

export interface PictureSourceProps {
  src: string;
  width: number;
  height?: number;
  media: string;
  fit?: FitOptions;
  sizes?: string;
  fetchPriority?: "high" | "low" | "auto";
}

export interface PictureProps extends Partial<Omit<ImageProps, "sizes">> {
  sources?: PictureSourceProps[];
  children?: ReactNode;
  className?: string;
}

export function Source({ src, width, height, media, fit = "cover", sizes, fetchPriority }: PictureSourceProps) {
  const srcSet = getSrcSet(src, width, height, fit);
  // Legacy parity: the Fresh Source emitted no `sizes`, so browsers fell back
  // to 100vw and picked the largest srcset candidate. Defaulting to
  // `${width}px` made 1x screens download the 1x candidate and upscale it —
  // visibly blurry banners (DECO-5585). Only forward explicit sizes.
  return (
    <source
      srcSet={srcSet}
      media={media}
      width={width}
      height={height}
      sizes={sizes}
      // @ts-expect-error React 19 supports fetchPriority on <source> at runtime
      fetchPriority={fetchPriority}
    />
  );
}

export function Picture({
  sources,
  src,
  width,
  height,
  fit = "cover",
  preload,
  className,
  children,
  ...rest
}: PictureProps) {
  return (
    <picture className={className}>
      {sources?.map((source, i) => (
        <Source key={i} {...source} fit={source.fit ?? fit} />
      ))}
      {children}
      {src && width !== undefined ? (
        <Image src={src} width={width} height={height} fit={fit} preload={preload} {...rest} />
      ) : null}
    </picture>
  );
}
